import { createStore } from 'vuex'
import type { ActionContext, Commit } from 'vuex'

import { i18n } from '@/i18n'
import * as API from '@/modules/matrix/api/matrixApi'
import { useMatrixNotify } from '@/modules/matrix/bridges/useMatrixNotify'
import { useMatrixSession } from '@/modules/matrix/bridges/useMatrixSession'
import type {
  Ceil,
  IBalance,
  IBuyBoosterParams,
  IMatrix,
  ListOfTypes,
  Type,
} from '@/modules/matrix/interfaces/store.interface'

import { createPartners } from './modules/partners'
import { createBoosters } from './modules/boosters'
import { createChains } from './modules/chains'
import { isAbortError, takeAbortSignal } from './requestAbort'
import type { MatrixStoreDependencies } from '../api/matrixClient'

/** Default matrix category for the GonkaGame "Chains" product. */
const DEFAULT_CATEGORY = 'gonka-game'

export interface RootState {
  selectedType: Type
  newTypeMatrix: string | null
  listOfTypes: ListOfTypes
  matrixByType: IMatrix
  /** Prefetched matrices keyed by type, for instant neighbour switching. */
  matrixCache: Record<string, IMatrix>
  matrixById: IMatrix
  balance: IBalance
  littleTabID: number
  bigTabID: number
}

export function createMatrixStore(dependencies: MatrixStoreDependencies) {
const { client: API, session: useMatrixSession, signal: takeAbortSignal } = dependencies
return createStore({
  modules: {
    partners: createPartners(dependencies),
    boosters: createBoosters(dependencies),
    chains: createChains(API),
  },
  state: (): RootState => ({
    selectedType: {} as Type,
    newTypeMatrix: null,
    listOfTypes: {} as ListOfTypes,
    matrixByType: {} as IMatrix,
    matrixCache: {},
    matrixById: {} as IMatrix,
    balance: {} as IBalance,
    littleTabID: 1,
    bigTabID: 1,
  }),
  actions: {
    getListOfTypes({ commit }: { commit: Commit }) {
      const category = DEFAULT_CATEGORY
      // Cache key is scoped per user (CR-MTX-07): a browser shared between
      // accounts must never replay another user's opened levels. When the
      // user id is not known yet, skip the cache read entirely.
      const { userId } = useMatrixSession()
      const cacheKey = (id: number) => `/api/matrix/list-of-types/${category}:${id}`
      const currentUserId = userId.value
      if (currentUserId !== null) {
        const cached = API.getDataFromLS<ListOfTypes>(cacheKey(currentUserId))
        if (cached) commit('SET_LIST_OF_TYPES', cached)
      }

      return API.getListOfTypes(category).then(response => {
        commit('SET_LIST_OF_TYPES', response.data)
        const id = userId.value
        if (id !== null) API.setDataToLS(cacheKey(id), response.data)
      })
    },
    async getMatrixByType(ctx: ActionContext<RootState, RootState>, matrixType: string) {
      if (!matrixType) return

      // Instant paint from the neighbour prefetch cache (if present) so a
      // matrix switch shows immediately; the backend refresh follows.
      const cached = ctx.state.matrixCache[matrixType]
      if (cached) ctx.commit('SET_MATRIX_BY_TYPE', cached)

      // Жёсткая отмена: новый запрос доски стопает предыдущий незавершённый
      // (запросы покинутых уровней физически обрываются).
      let response
      try {
        response = await API.getMatrix(matrixType, { signal: takeAbortSignal('board') })
      } catch (error) {
        if (isAbortError(error)) return
        throw error
      }
      if (response?.data) {
        ctx.commit('SET_LIST_OF_TYPES', response.data.tabs)
        delete response.data.tabs
        ctx.commit('SET_MATRIX_CACHE', { type: matrixType, data: response.data })
        // Страховка от гонки: ответ уже покинутого уровня не должен
        // перерисовать доску текущего — он обновляет только кэш.
        const currentType = ctx.state.selectedType?.type
        if (!currentType || currentType === matrixType) {
          ctx.commit('SET_MATRIX_BY_TYPE', response.data)
        }
        return response
      }
    },
    // Background-load a matrix into the cache WITHOUT touching the visible slot,
    // so adjacent levels (F(n±1)) are ready before the user scrolls there.
    // Always refetches: a cached neighbour may be stale (visited earlier), and
    // the peek/instant-paint must show fresh cells for BOTH n-1 and n+1.
    async prefetchMatrixByType(ctx: ActionContext<RootState, RootState>, matrixType: string) {
      if (!matrixType) return

      const response = await API.getMatrix(matrixType)
      if (response?.data) {
        delete response.data.tabs
        ctx.commit('SET_MATRIX_CACHE', { type: matrixType, data: response.data })
      }
    },
    async getMatrixByID(ctx: ActionContext<RootState, RootState>, matrixID: string) {
      if (!matrixID) return

      // Ключ 'view': быстрый кликер по цепочке просмотра стопает предыдущий запрос
      const response = await API.getMatrix(matrixID, { signal: takeAbortSignal('view') })
      if (response?.data) {
        delete response.data.tabs
        ctx.commit('SET_MATRIX_BY_ID', response.data)
        return response
      }
    },
    async getMatrixByUUID(ctx: ActionContext<RootState, RootState>, matrixUUID: string) {
      if (!matrixUUID) return

      const response = await API.getMatrixByUUID(matrixUUID, { signal: takeAbortSignal('view') })
      if (response?.data) {
        delete response.data.tabs
        ctx.commit('SET_MATRIX_BY_ID', response.data)
        return response
      }
    },
    async buyBooster(_ctx: ActionContext<RootState, RootState>, data: IBuyBoosterParams) {
      const notificationText = data?.notificationText
      delete data.notificationText

      const response = await API.placementClone(data)
      if (response?.error_code === undefined) {
        useMatrixNotify().success(notificationText ?? i18n.global.t('matrix.notify.boostSent'))
      }

      return response
    },
    async getWallets({ commit }: { commit: Commit }) {
      const response = await API.getBalance()
      commit('SET_WALLETS', response.data)

      return response
    },
  },
  getters: {
    onlyInfinityCell(state: RootState) {
      const objCells = state.matrixByType?.ceilsCollection?.['1']
      const cells = objCells ? Object.values(objCells) : []
      return cells.length === 1 && Boolean((cells[0] as Ceil | undefined)?.isInfinity)
    },
  },
  mutations: {
    SET_LIST_OF_TYPES(state: RootState, list: ListOfTypes) {
      state.listOfTypes = list
    },
    SET_MATRIX_BY_TYPE(state: RootState, data: IMatrix) {
      state.matrixByType = data
    },
    SET_MATRIX_CACHE(state: RootState, payload: { type: string; data: IMatrix }) {
      state.matrixCache = { ...state.matrixCache, [payload.type]: payload.data }
    },
    SET_MATRIX_BY_ID(state: RootState, data: IMatrix) {
      state.matrixById = data
    },
    SET_SELECTED_TYPE(state: RootState, type: Type) {
      state.selectedType = type
    },
    SET_NEW_TYPE_MATRIX(state: RootState, type: string | null) {
      state.newTypeMatrix = type
    },
    SET_WALLETS(state: RootState, wallets: IBalance) {
      state.balance = wallets
    },
    CHANGE_LITTLE_TAB(state: RootState, id: number) {
      state.littleTabID = id
    },
    CHANGE_BIG_TAB(state: RootState, id: number) {
      state.bigTabID = id
    },
  },
})
}

export default createMatrixStore({ client: API, session: useMatrixSession, signal: takeAbortSignal })
